import {
    CheckoutLifecycleEventPostMessageName,
    checkoutPostMessage,
} from '@eventbrite/checkout-postmessage-api';
import { Button } from '@eventbrite/eds-button';
import { formValidate } from '@eventbrite/eds-utils';
import { GenericLazyString, gettext } from '@eventbrite/i18n';
import { logEvent } from '@eventbrite/statsig';
import loadable from '@loadable/component';
import classnames from 'classnames';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { FooterError } from '../../components/FooterError';
import { FullPageLoadingLayer } from '../../components/fullPageLoadingLayer/FullPageLoadingLayer';
import {
    default as TicketCategoryGroup,
    TicketClassGroupProps,
} from '../../components/ticketsSelectionList/TicketCategoryGroup/TicketCategoryGroup';
import { TICKETS_PAGE_FORM_NAME } from '../../constants';
import { FOCUSABLE_FORM_ELEMENTS } from '../../constants/focusableElements';
import { STATSIG_EVENT_NAMES } from '../../constants/statsig';
import { ParsedError } from '../../models/error';
import { Ticket, TicketId } from '../../models/tickets';
import { resetFocusToInitialElement } from '../../utils/accessibility';
import ErrorNotification from './ErrorNotification';
import './TicketSelectionCompactSizePage.scss';
import { getCtaTextIncludingTotalCost } from './utils/getCtaTextIncludingTotalCost';
import validate from './validation';

const ConnectedExpressCheckout = loadable(
    () => import('../../components/ExpressCheckout/ConnectedExpressCheckout'),
);

export interface TicketSelectionCompactSizePageProps {
    areTicketsSelected: boolean;
    errors: Array<ParsedError>;
    canMakePaymentsWithActiveCard?: boolean;
    isDisabled: boolean;
    isExpressApplePayEnabled: boolean;
    isFreeEvent: boolean;
    isMultiTicket: boolean;
    isMakingApplePayPayment: boolean;
    isApplePayElegible: boolean;
    onPageWillMount: () => void;
    onQuantityChange: (data: { id: string; quantity: number }) => void;
    onSubmit: (values: Record<string, unknown>) => void;
    onTicketSelectionLayoutStabilized: (pageHeight?: number) => void;
    selectedTickets: Record<TicketId, Ticket>;
    /**
     * A common sales start or end date for all tickets, if it exists
     */
    salesDateForAllTickets?: GenericLazyString | string;
    /**
     * Whether to show the headings for ticket class category groupings
     */
    shouldShowTicketCategoryGroupHeaders?: boolean;
    /**
     * A sales status (sales end soon, sales not started, etc.) that applies to all tickets, if it exists
     */
    statusForAllTickets?: string;
    /**
     * Props related to the checkout template
     */
    templateProps: {
        isReservedSeating: boolean;
        totalTicketQuantity: number;
        isSubmitting: boolean;
    };
    /**
     * Ticket classes sorted by category
     */
    ticketClassesByCategory: Record<string, TicketClassGroupProps>;
    /**
     * The total cost to display in the footer.
     */
    totalCostDisplay: string;
    /**
     * Node displaying any validation error next to the submit button.
     * Note that this node will be rendered only when the form has been submitted at least once.
     */
    validationErrorMessage?: GenericLazyString | string;
    /**
     * Denotes that this app is being rendered inside an iframe, giving the url of its parent page
     */
    widgetParentUrl?: string;

    /**
     * Props passed by redux-form
     */
    error: GenericLazyString | string;
    handleSubmit: (
        callback: (values: Record<string, unknown>) => void,
    ) => () => void;
    submitFailed: boolean;
    areExpressCheckoutConditionsReady: boolean;
}

export const TicketSelectionCompactSizePage: FunctionComponent<
    TicketSelectionCompactSizePageProps
> = ({
    areExpressCheckoutConditionsReady,
    areTicketsSelected,
    error,
    errors = [],
    canMakePaymentsWithActiveCard,
    handleSubmit,
    isApplePayElegible,
    isDisabled,
    isExpressApplePayEnabled,
    isFreeEvent,
    isMakingApplePayPayment,
    isMultiTicket,
    onPageWillMount,
    onQuantityChange,
    onSubmit,
    onTicketSelectionLayoutStabilized,
    selectedTickets,
    shouldShowTicketCategoryGroupHeaders,
    submitFailed,
    templateProps,
    ticketClassesByCategory,
    totalCostDisplay,
    validationErrorMessage,
    widgetParentUrl,
}) => {
    const focusRef = useRef<HTMLDivElement>(null);
    const pageWrapperRef = useRef<HTMLDivElement>(null);
    const resizeObserver = useRef<ResizeObserver>();

    const [hasSubmittedForm, setHasSubmittedForm] = useState<boolean>(false);
    const [isExpressCheckoutStabilized, setIsExpressCheckoutStabilized] =
        useState<boolean>(false);
    const [hasCalledBack, setHasCalledBack] = useState<boolean>(false);

    const [pageHeight, setPageHeight] = useState<number | null>(null);

    useEffect(() => {
        onPageWillMount();
        if (widgetParentUrl) {
            checkoutPostMessage({
                messageName:
                    CheckoutLifecycleEventPostMessageName.ticketSelectionCompactLoaded,
                widgetParentUrl,
            });
        }
    }, [onPageWillMount, widgetParentUrl]);

    const shouldRenderExpressCheckoutButtons =
        isExpressApplePayEnabled && isApplePayElegible;

    // This effect is to notify a stabilized layout when there is no express checkout
    useEffect(() => {
        if (pageHeight === null) {
            return;
        }

        if (!areExpressCheckoutConditionsReady) {
            return;
        }

        if (shouldRenderExpressCheckoutButtons) {
            return;
        }

        if (hasCalledBack) {
            return;
        }

        onTicketSelectionLayoutStabilized(pageHeight);
        setHasCalledBack(true);
    }, [
        areExpressCheckoutConditionsReady,
        onTicketSelectionLayoutStabilized,
        shouldRenderExpressCheckoutButtons,
        pageHeight,
        hasCalledBack,
    ]);

    // This effect is to notify resizes and when the layout is stabilized after a resize when there is express checkout
    useEffect(() => {
        const updatePageHeight = (currentPageHeight: number) => {
            if (pageHeight === currentPageHeight) {
                return;
            }

            setPageHeight(currentPageHeight);

            const message = {
                messageName:
                    CheckoutLifecycleEventPostMessageName.ticketSelectionResize,
                additionalData: { contentHeight: currentPageHeight },
                widgetParentUrl,
            };

            checkoutPostMessage(message, { sendDictionaryToiOS: true });

            if (isExpressCheckoutStabilized && !hasCalledBack) {
                onTicketSelectionLayoutStabilized(currentPageHeight);
                setHasCalledBack(true);
            }
        };

        resizeObserver.current = new ResizeObserver((entries) =>
            updatePageHeight(entries[0].target.clientHeight),
        );

        if (pageWrapperRef.current) {
            resizeObserver.current.observe(pageWrapperRef.current);
        }

        return () => {
            if (resizeObserver.current) {
                resizeObserver.current.disconnect();
            }
        };
    }, [
        pageHeight,
        widgetParentUrl,
        isExpressCheckoutStabilized,
        onTicketSelectionLayoutStabilized,
        hasCalledBack,
    ]);

    const _onSubmit = (data: { statsigValue?: string | number | null }) => {
        logEvent(
            STATSIG_EVENT_NAMES.TICKET_STEP_CONTINUE_TO_PAYMENT_CLICK,
            data.statsigValue,
            {
                isCompactSizeFlow: 'true',
            },
        );
        if (!hasSubmittedForm && validationErrorMessage) {
            logEvent(STATSIG_EVENT_NAMES.TICKET_STEP_VALIDATION_ERROR_VIEW);
        }

        setHasSubmittedForm(true);

        if (validationErrorMessage && focusRef.current) {
            resetFocusToInitialElement({
                rootEl: focusRef.current,
                focusableEls: FOCUSABLE_FORM_ELEMENTS,
            });

            return;
        }

        handleSubmit((values) =>
            onSubmit({
                ...values,
                ...data,
            }),
        )();
    };

    const ctaText = getCtaTextIncludingTotalCost({
        isFreeEvent,
        areTicketsSelected,
        totalCostDisplay,
        selectedTickets,
    });

    const errorDescription = error && submitFailed ? error : null;
    const errorNotificationsNode =
        errors.length || errorDescription ? (
            <ErrorNotification
                errorDescription={errorDescription}
                errors={errors}
            />
        ) : null;

    const ticketClassCategories = Object.keys(ticketClassesByCategory);

    const footerMessageNode =
        hasSubmittedForm && validationErrorMessage ? (
            <div className="eds-l-mar-vert-4">
                <FooterError className="ticket-selection-compact-error">
                    {validationErrorMessage}
                </FooterError>
            </div>
        ) : null;

    const checkoutButtons = shouldRenderExpressCheckoutButtons ? (
        <ConnectedExpressCheckout
            handleSubmit={_onSubmit}
            isSubmitting={templateProps.isSubmitting}
            onFullyLoaded={() => {
                setIsExpressCheckoutStabilized(true);
            }}
        />
    ) : (
        <Button
            onClick={_onSubmit}
            style="fill"
            disabled={isDisabled}
            data-spec="eds-modal__primary-button"
            data-automation="eds-modal__primary-button"
            isLoading={templateProps.isSubmitting}
            size="block"
        >
            {ctaText}
        </Button>
    );

    const sectionCssClasses = classnames('ticket-selection-compact', {
        ['ticket-selection-compact-multiTicket']: isMultiTicket,
    });

    const footerCssClasses = classnames({
        ['ticket-selection-compact-multiTicket-checkout-button']: isMultiTicket,
    });
    const fullPageLoadingLayer = isExpressApplePayEnabled ? (
        <FullPageLoadingLayer
            isShown={templateProps.isSubmitting && isMakingApplePayPayment}
        >
            <div
                className="eds-text-bl eds-mar-top-5"
                data-testid="compact-size-loading-text"
            >
                {canMakePaymentsWithActiveCard ? gettext('Placing order') : ''}
            </div>
        </FullPageLoadingLayer>
    ) : null;

    return (
        <>
            {fullPageLoadingLayer}
            <section
                className={sectionCssClasses}
                data-spec="ticket-selection-compact"
                ref={pageWrapperRef}
            >
                <div ref={focusRef} data-spec="focus-ref" />
                <form onSubmit={_onSubmit as any} className="eds-l-mar-bot-4">
                    {ticketClassCategories.map((groupKey, i) => (
                        <TicketCategoryGroup
                            isMultiTicket={isMultiTicket}
                            key={i}
                            onQuantityChange={onQuantityChange}
                            shouldShowTicketCategoryGroupHeaders={
                                shouldShowTicketCategoryGroupHeaders
                            }
                            ticketClassGroupProps={
                                ticketClassesByCategory[groupKey]
                            }
                            totalCostDisplay={totalCostDisplay}
                        />
                    ))}
                </form>

                <div className={footerCssClasses}>
                    {errorNotificationsNode || footerMessageNode}
                    {checkoutButtons}
                </div>
            </section>
        </>
    );
};

export default formValidate({
    form: TICKETS_PAGE_FORM_NAME,
    validate,
})(TicketSelectionCompactSizePage);
